<template>
  <div class="flex flex-col w-full h-full gap-2 overflow-y-auto">
    <span class="mb-1 font-medium">בחירת לקוח/ה</span>
    <SelectCustomer
      v-model="selectedCustomerId"
      @customer-selected="onCustomerSelected" />

    <div class="mt-4 p-3 rounded-lg bg-primary-50 flex gap-3 items-start">
      <i class="pi pi-info-circle text-primary mt-0.5" />
      <span class="text-sm text-surface-600">
        הלקוח/ה יקבל/תקבל SMS עם קישור אישי לעמוד המועדון — שם מוצגות ההטבות ותנאי המועדון,
        וניתן לאשר את ההצטרפות ולמלא את תאריך יום ההולדת.
      </span>
    </div>

    <span v-if="alreadyMemberWarning" class="text-sm text-amber-600 mt-2">
      הלקוח/ה כבר חבר/ה במועדון הלקוחות
    </span>

    <drawer-footer
      class="mt-auto"
      approve-label="שליחת הזמנה"
      :approveDisable="!selectedCustomer"
      :approveLoading="loadingCta"
      @approve-click="sendInvite"
      @cancel-click="$emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { ICustomer } from '@torim-app/types';
import { useToast } from 'vue-toastification';
import { useDrawer } from '@torim-app/shared-components';
import { LoyaltyProgramApi } from '@app/api/admin/loyalty-program-api';
import { trackEvent } from '@app/composables/posthog';

const emit = defineEmits(['invited', 'close']);

const toast = useToast();
const { closeDrawer } = useDrawer();

type SelectableCustomer = Pick<ICustomer, 'id' | 'fullName' | 'tags' | 'loyalty' | 'smsOptedOut' | 'phoneNumber'>;

const selectedCustomerId = ref<string | undefined>();
const selectedCustomer = ref<SelectableCustomer | undefined>();
const alreadyMemberWarning = ref(false);
const loadingCta = ref(false);

const onCustomerSelected = (customer: SelectableCustomer | undefined) => {
  selectedCustomer.value = customer;
  // Surface club membership as soon as the customer is picked, instead of letting
  // the owner send an invite that the server would reject anyway.
  alreadyMemberWarning.value = !!customer?.loyalty;
};

const sendInvite = async () => {
  if (loadingCta.value || !selectedCustomer.value) return;
  trackEvent('loyalty_program_invite_sent');
  loadingCta.value = true;
  try {
    const res = await LoyaltyProgramApi.sendJoinInvite(selectedCustomer.value.id);
    if (res.alreadyMember) {
      alreadyMemberWarning.value = true;
      return;
    }
    if (res.smsOptedOut)
      toast.info('הלקוח/ה ביקש/ה לא לקבל הודעות SMS — ניתן לשלוח את הקישור מכרטיס הלקוח בוואטסאפ');
    else
      toast.success('ההזמנה נשלחה ללקוח/ה');
    emit('invited');
    closeDrawer();
  } catch (err) {
    console.log(err);
    toast.error('לא הצלחנו לשלוח את ההזמנה, אנא נסו שוב');
  } finally {
    loadingCta.value = false;
  }
};
</script>
